export const SITE_DEFAULTS = {
  title: 'Medidata esPublico | Soluções Tecnológicas para a Administração Pública Local',
  description: 'Medidata esPublico disponibiliza soluções tecnológicas para a Administração Pública Local, promovendo a transformação digital, a interoperabilidade e a modernização dos serviços públicos.',
  siteName: 'Medidata esPublico',
  legalName: 'Medidata esPublico, Unipessoal Lda.',
  imageAlt: 'Logótipo Medidata esPublico',
  baseUrl: 'https://www.medidata.pt',
  defaultLanguage: 'pt',
  logo: 'https://www.medidata.pt/static/images/logo_medidata.svg',
  ogImage: 'https://www.medidata.pt/static/images/imagem_partilha.png',
};

const hasMetaContent = (meta) => {
  if (!meta || typeof meta !== 'object') return false;
  const { title, description, keywords } = meta;
  return !!(title || description || (keywords && keywords.length));
};

export const buildCanonicalUrl = (language = SITE_DEFAULTS.defaultLanguage, slug) => {
  const lang = language || SITE_DEFAULTS.defaultLanguage;

  if (!slug || slug === '/') {
    return `${SITE_DEFAULTS.baseUrl}/${lang}/`;
  }

  const cleanSlug = slug.startsWith('/') ? slug.slice(1) : slug;
  return `${SITE_DEFAULTS.baseUrl}/${lang}/${cleanSlug}`;
};

export const buildCanonicalUrlFromPath = (pathname) => {
  let path = pathname || `/${SITE_DEFAULTS.defaultLanguage}/`;

  if (path === '/') {
    path = `/${SITE_DEFAULTS.defaultLanguage}/`;
  } else if (!path.endsWith('/')) {
    path = `${path}/`;
  }

  return `${SITE_DEFAULTS.baseUrl}${path}`;
};

const setMetaById = (id, content) => {
  const element = document.getElementById(id);
  if (element) {
    element.setAttribute('content', content);
  }
};

const setMetaByProperty = (property, content) => {
  const element = document.querySelector(`meta[property="${property}"]`);
  if (element) {
    element.setAttribute('content', content);
  }
};

const setCanonical = (href) => {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

const setHtmlLang = (language) => {
  if (language) {
    document.documentElement.setAttribute('lang', language);
  }
};

export const applySiteDefaults = (options = {}) => {
  insertMetatags({}, {
    ...options,
    forceDefaults: true,
  });
};

export const insertMetatags = (meta, options = {}) => {
  const {
    language = SITE_DEFAULTS.defaultLanguage,
    canonicalUrl,
    forceDefaults = false,
  } = options;

  const resolved = (forceDefaults || !hasMetaContent(meta))
    ? {
      title: SITE_DEFAULTS.title,
      description: SITE_DEFAULTS.description,
      keywords: meta?.keywords,
    }
    : meta;

  const { title, description, keywords } = resolved;
  const canonical = canonicalUrl || buildCanonicalUrlFromPath(window.location.pathname);

  if (title) {
    document.title = title;
    setMetaById('ogtitle', title);
  }

  if (description) {
    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (!descriptionMeta) {
      descriptionMeta = document.createElement('meta');
      descriptionMeta.setAttribute('name', 'description');
      document.head.appendChild(descriptionMeta);
    }
    descriptionMeta.setAttribute('content', description);
    setMetaById('ogdescription', description);
  }

  if (keywords && keywords.length) {
    setMetaById('keywords', keywords.toString());
  } else if (forceDefaults) {
    setMetaById('keywords', '');
  }

  setMetaById('ogurl', canonical);
  setMetaByProperty('og:site_name', SITE_DEFAULTS.siteName);
  setMetaById('ogsite_name', SITE_DEFAULTS.siteName);
  setMetaById('ogimagealt', SITE_DEFAULTS.imageAlt);
  setCanonical(canonical);
  setHtmlLang(language);
};

export const updateOrganizationSchema = ({ sameAs = [] } = {}) => {
  const validSameAs = sameAs.filter(Boolean);
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_DEFAULTS.siteName,
    legalName: SITE_DEFAULTS.legalName,
    url: `${SITE_DEFAULTS.baseUrl}/${SITE_DEFAULTS.defaultLanguage}/`,
    logo: SITE_DEFAULTS.logo,
  };

  if (validSameAs.length) {
    schema.sameAs = validSameAs;
  }

  let script = document.getElementById('organization-schema');
  if (!script) {
    script = document.createElement('script');
    script.id = 'organization-schema';
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(schema);
};
